import { FontSizeOutlined } from "@ant-design/icons";
import { Input, Tooltip } from "antd";
import { type ComponentProps, type FocusEvent, useState } from "react";
import { useTranslation } from "react-i18next";
import { useRefreshState } from "../context/RefreshContext.tsx";
import { clickable } from "../styles/shared.styles.ts";
import { normalizeString as normalize } from "../utils/stringNormalize.ts";

const toSentenceCase = (s: string, locale: string) =>
  s.length === 0 ? s : s[0].toLocaleUpperCase(locale) + s.slice(1).toLocaleLowerCase(locale);
const toTitleCase = (s: string, locale: string) =>
  s.replace(/\S+/g, (w) => w[0].toLocaleUpperCase(locale) + w.slice(1).toLocaleLowerCase(locale));
const toLowerCase = (s: string, locale: string) => s.toLocaleLowerCase(locale);
const toUpperCase = (s: string, locale: string) => s.toLocaleUpperCase(locale);

const caseTransforms = [
  { fn: toSentenceCase, key: "caseToggle.sentenceCase" }, // t("caseToggle.sentenceCase")
  { fn: toTitleCase, key: "caseToggle.titleCase" }, // t("caseToggle.titleCase")
  { fn: toLowerCase, key: "caseToggle.lowerCase" }, // t("caseToggle.lowerCase")
  { fn: toUpperCase, key: "caseToggle.upperCase" }, // t("caseToggle.upperCase")
] as const;

type InputProps = ComponentProps<typeof Input>;
type TextAreaProps = ComponentProps<typeof Input.TextArea>;

type AutoSaveInputProps = Omit<InputProps, "onBlur"> & {
  readonly onCommit?: (value: string) => void;
  readonly showCaseToggle?: boolean;
};

type AutoSaveTextAreaProps = Omit<TextAreaProps, "onBlur"> & {
  readonly onCommit?: (value: string) => void;
};

export const AutoSaveInput = ({
  onCommit = undefined,
  defaultValue,
  showCaseToggle = false,
  ...props
}: AutoSaveInputProps) => {
  const { t, i18n } = useTranslation();
  const [value, setValue] = useRefreshState(defaultValue);
  const [caseIndex, setCaseIndex] = useState(0);

  const handleBlur = (e: FocusEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    const normalizedValue = normalize(newValue);
    if (newValue !== normalizedValue && normalizedValue == defaultValue) {
      setValue(normalizedValue);
    } else {
      if (onCommit && newValue !== defaultValue) {
        onCommit(normalizedValue);
      }
    }
  };

  const handleCaseToggle = () => {
    const current = String(value ?? "");
    if (current.length === 0) return;
    const { fn } = caseTransforms[caseIndex];
    const transformed = fn(current, i18n.language);
    setCaseIndex((caseIndex + 1) % caseTransforms.length);
    setValue(transformed);
    if (onCommit && transformed !== String(defaultValue ?? "")) {
      onCommit(transformed);
    }
  };

  const caseSuffix = showCaseToggle ? (
    <Tooltip destroyOnHidden title={t(caseTransforms[caseIndex].key)}>
      <FontSizeOutlined
        className={clickable}
        onClick={handleCaseToggle}
        style={{ color: "var(--ant-color-text-quaternary)" }}
      />
    </Tooltip>
  ) : undefined;

  return (
    <Input
      onBlur={handleBlur}
      onChange={(c) => setValue(c.target.value)}
      suffix={caseSuffix}
      value={value}
      // eslint-disable-next-line react/jsx-props-no-spreading
      {...props}
    />
  );
};

// eslint-disable-next-line react/no-multi-comp
export const AutoSaveTextArea = ({ onCommit = undefined, defaultValue, ...props }: AutoSaveTextAreaProps) => {
  const [value, setValue] = useRefreshState(defaultValue);

  const handleBlur = (e: FocusEvent<HTMLTextAreaElement>) => {
    const newValue = e.target.value;
    const normalizedValue = normalize(newValue);
    if (newValue !== normalizedValue && normalizedValue == defaultValue) {
      setValue(normalizedValue);
    } else {
      if (onCommit && newValue !== defaultValue) {
        onCommit(normalizedValue);
      }
    }
  };

  // eslint-disable-next-line react/jsx-props-no-spreading
  return <Input.TextArea onBlur={handleBlur} onChange={(c) => setValue(c.target.value)} value={value} {...props} />;
};
